import React from 'react';

import GlobalityLabelCDS from '@sharedCR/coreDesignSystem/GlobalityLabelCDS';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { formsApi } from 'app/services/api';
import { formatFileSize } from 'app/shared/components/TeamChat/TeamChat.utils';
import {
  AuditOperation,
  DEFAULT_HISTORY_TITLE,
  EMPTY_FIELD_LIST,
} from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.constants';
import {
  formatRowDateTime,
  getOperationLabel,
  sortEntriesByTime,
  toOperation,
} from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.utils';
import _ from 'lodash';

import { SortOrder } from 'constants/data';
import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { formatDate } from 'utils/date';
import { getDynamicFieldCatalogName } from 'utils/forms';
import { safeJsonParse } from 'utils/json';

import {
  COMPARE_WITH_CURRENT_LABEL,
  MARK_PANES_BY_ROW_KIND,
  PART_GROUP_SINGULAR,
  PREVIEW_PANES,
  FIELD_MAPPING_TITLE,
  FileFactLabel,
  FORM_HISTORY_HIDDEN_FIELDS,
  FORM_OVERRIDE_AUDIT_ENTITY_TYPE,
  FormHistoryChip,
  FormSummaryKind,
  FormTemplateField,
  HtmlChange,
  HTML_TEMPLATE_FIELD_SELECTOR,
  HTML_TEMPLATE_FIELD_SLOT_SELECTOR,
  HtmlTemplateFieldAttribute,
  HtmlTemplateFieldType,
  ANOTHER_BLOCK_NOTE,
  CONTAINER_PREVIEW_LENGTH,
  FORMATTING_CHANGED_NOTE,
  IMAGE_PART_NAME,
  IN_TABLE_NOTE,
  PART_ROW_KIND_BY_CHANGE,
  PART_ROW_KIND_LABEL,
  PART_ROW_KINDS,
  PartRowKind,
  REBOUND_NOTE,
  TABLE_NODE_TYPE,
  EMPTY_PARAGRAPH_LABEL,
  FILE_MODAL_TITLE,
  IN_EFFECT_NOTE,
  LOCAL_TIME_OPTIONS,
  NO_PART_CHANGES_MESSAGE,
  NO_PARTS_LABEL,
  NOT_EFFECTIVE_LABEL,
  NOT_EFFECTIVE_NOTE,
  OVERRIDE_LIFECYCLE_NOTE,
  PART_CHANGE_LABEL,
  PART_CHANGE_VARIANT,
  POSITION_DECIMALS,
  PREVIEW_MODAL_TITLE,
  PREVIEW_PANE_TITLE,
  PRISM_STATUS_MESSAGE,
  REGION_LABEL,
  SHORT_CHECKSUM_LENGTH,
  TEMPLATE_COMPANION_FIELD,
  TEMPLATE_PART_GROUPS,
  TEMPLATE_SIDES,
  TEMPLATE_SIDE_LABEL,
  TEMPLATE_ERROR_STATUS,
  TemplateChangeKind,
  TemplatePartChange,
  TemplatePartGroup,
  TemplateRegion,
  TemplateRequestStatus,
  TemplateSide,
  SIGNATURE_FIELD_LABEL,
  UNKNOWN_DYNAMIC_FIELD_LABEL,
  VERSION_PANE_TITLE,
  YOUR_OVERRIDE_LABEL,
} from './FormChangeHistory.constants';
import { FormChangeHistoryTestIds } from './FormChangeHistory.testIds';

const OVERRIDE_CHIP = (
  <LabelCDS
    withBorder
    dataTestId={FormChangeHistoryTestIds.OVERRIDE_CHIP}
    key={FormHistoryChip.OVERRIDE}
    size={LabelSize.S}
    title={YOUR_OVERRIDE_LABEL}
    variant={LabelVariant.GRAY}
  />
);

const GLOBAL_CHIP = <GlobalityLabelCDS isGlobal key={FormHistoryChip.GLOBAL} size={LabelSize.S} />;

const NOT_EFFECTIVE_CHIP = (
  <LabelCDS
    dataTestId={FormChangeHistoryTestIds.NOT_EFFECTIVE_CHIP}
    key={FormHistoryChip.NOT_EFFECTIVE}
    size={LabelSize.S}
    title={NOT_EFFECTIVE_LABEL}
    variant={LabelVariant.GRAY}
  />
);

const PLAIN_DECORATION = { chips: null, muted: false, note: null, mutedFields: EMPTY_FIELD_LIST };

/**
 * Scope marks only mean something where the viewer's form can differ from the Global one: a
 * company user looking at a global form. Internal users and company-owned forms get the plain view.
 */
export const getIsScopedFormHistory = ({ isGlobalForm, isInternalViewer }) =>
  !!isGlobalForm && !isInternalViewer;

// The override's start is unknown when its create row was purged; the note then has no date.
const getNotEffectiveNote = overrideFromUtc => {
  if (!overrideFromUtc) {
    return NOT_EFFECTIVE_NOTE;
  }

  const overrideFromDate = formatDate(
    overrideFromUtc,
    SHORT_MONTH_DAY_YEAR_FORMAT,
    LOCAL_TIME_OPTIONS,
  );

  return `Your override replaced it on ${overrideFromDate}, so this never reached your form.`;
};

const getOverrideRowDecoration = ({ operation }) => ({
  ...PLAIN_DECORATION,
  chips: [OVERRIDE_CHIP],
  note: OVERRIDE_LIFECYCLE_NOTE[toOperation(operation)] ?? null,
});

const getGlobalRowDecoration = ({ shadowing }) => {
  if (!shadowing) {
    return { ...PLAIN_DECORATION, chips: [GLOBAL_CHIP] };
  }

  if (shadowing.notEffective) {
    return {
      ...PLAIN_DECORATION,
      chips: [GLOBAL_CHIP, NOT_EFFECTIVE_CHIP],
      muted: true,
      note: getNotEffectiveNote(shadowing.overrideFromUtc),
    };
  }

  return {
    ...PLAIN_DECORATION,
    chips: [GLOBAL_CHIP],
    note: IN_EFFECT_NOTE,
    mutedFields: shadowing.shadowedFields ?? EMPTY_FIELD_LIST,
  };
};

/** `getRowDecoration` for a scoped history: the company's override rows vs the Global form's rows. */
export const getFormRowDecoration = row =>
  row.entityType === FORM_OVERRIDE_AUDIT_ENTITY_TYPE
    ? getOverrideRowDecoration(row)
    : getGlobalRowDecoration(row);

export const getFormHistoryDrawerTitle = formName => formName || DEFAULT_HISTORY_TITLE;

// A Create writes every template field, most of them empty, so in the drill-in a companion is hidden
// only when its template field holds something on either side.
const hasWrittenTemplate = (field, changedFields, detail) =>
  _.includes(changedFields, field) &&
  (!detail || hasTemplateOnEitherSide(_.find(detail.changes, { field })?.summary));

// In the drill-in, a template field with nothing on either side has nothing for the template panel.
const getEmptyTemplateFields = detail =>
  _.filter(
    detail?.changes,
    ({ summary }) => isTemplateSummary(summary) && !hasTemplateOnEitherSide(summary),
  ).map(({ field }) => field);

/**
 * `hiddenFields` per row: always the metadata path, plus the companion of a template field it wrote
 * and, in the drill-in, every template field left empty on both sides.
 */
export const getFormHiddenFields = ({ changedFields }, detail = null) => [
  ...FORM_HISTORY_HIDDEN_FIELDS,
  ..._.compact(
    _.map(TEMPLATE_COMPANION_FIELD, (companion, field) =>
      hasWrittenTemplate(field, changedFields, detail) ? companion : null,
    ),
  ),
  ...getEmptyTemplateFields(detail),
];

// A failed read is forgotten so that a retry reads again.
const memoizeRequest = (request, resolveKey) => {
  const memoized = _.memoize(
    (...args) =>
      request(...args).catch(error => {
        memoized.cache.delete(resolveKey(...args));

        throw error;
      }),
    resolveKey,
  );

  return memoized;
};

const getResponseData = ({ data }) => data;

/** How many times each read was retried, and who to tell about the next retry. */
const createRetryCounts = () => {
  const counts = new Map();
  const listeners = new Set();

  return {
    getCount: key => counts.get(key) ?? 0,
    retry: key => {
      counts.set(key, (counts.get(key) ?? 0) + 1);
      listeners.forEach(listener => listener());
    },
    subscribe: listener => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
  };
};

// The live form's documents and preview do not depend on the change they were asked from.
const getSideKey = (auditId, side) => (side === TemplateSide.CURRENT ? side : `${auditId}:${side}`);

const getCurrentTemplateJson = formId =>
  formsApi
    .getFormTemplateJson({ formId })
    .then(({ data }) => safeJsonParse(data?.document ?? null));

/**
 * The drawer's reads of one form's template versions; `side` is a change's before / after, or the
 * form as the reader sees it now (current). A change's template changes, documents and previews are
 * read once: reopening a row, a tab or a preview reuses the answer. Files are read on demand. A
 * compare reads the current form again each time, and so does the next read of the current form's
 * document and preview after it: the form may have been saved since. Retries of a change's
 * template changes are counted here, so every reader of them reads again.
 */
export const createTemplateRequests = formId => {
  const getJson = memoizeRequest(
    (auditId, side) =>
      side === TemplateSide.CURRENT
        ? getCurrentTemplateJson(formId)
        : formsApi.getFormAuditTemplateJson({ formId, auditId, side }).then(getResponseData),
    getSideKey,
  );
  const getPreviewPdf = memoizeRequest(
    (auditId, side) =>
      formsApi.getFormAuditPreviewPdf({ formId, auditId, side }).then(getResponseData),
    getSideKey,
  );

  return {
    getChanges: memoizeRequest(
      auditId => formsApi.getFormAuditTemplateChanges({ formId, auditId }).then(getResponseData),
      auditId => auditId,
    ),
    getJson,
    getFile: (auditId, side) => formsApi.getFormAuditTemplateFile({ formId, auditId, side }),
    getCompare: auditId => {
      getJson.cache.delete(TemplateSide.CURRENT);
      getPreviewPdf.cache.delete(TemplateSide.CURRENT);

      return formsApi.getFormAuditCompare({ formId, auditId }).then(getResponseData);
    },
    getPreviewPdf,
    changesRetries: createRetryCounts(),
  };
};

export const getTemplateRequestErrorStatus = (error, errorStatuses = TEMPLATE_ERROR_STATUS) =>
  errorStatuses[_.get(error, 'response.status')] ?? TemplateRequestStatus.FAILED;

const hasFileSide = ({ hasOld, hasNew }) => !!hasOld || !!hasNew;

const SUMMARY_HAS_TEMPLATE = {
  [FormSummaryKind.PRISM_TEMPLATE]: data => !_.isNil(data.old) || !_.isNil(data.new),
  [FormSummaryKind.PDF_FILE]: hasFileSide,
  [FormSummaryKind.HTML_FILE]: hasFileSide,
  [FormSummaryKind.PDF_FIELD_MAPPING]: data =>
    !_.isNil(data.oldFieldCount) ||
    !_.isNil(data.newFieldCount) ||
    !!data.oldUnreadable ||
    !!data.newUnreadable,
};

const isTemplateSummary = summary => _.has(SUMMARY_HAS_TEMPLATE, summary?.kind);

/** Whether either side of a summarised template field holds something; a Create writes them all. */
export const hasTemplateOnEitherSide = summary =>
  !!SUMMARY_HAS_TEMPLATE[summary?.kind]?.(summary.data ?? {});

/**
 * A template-changes read's status for a renderer: empty when neither side holds a template, and an
 * answer about another kind of template than `kind` is not loaded data.
 */
export const getTemplateChangesStatus = ({ hasTemplate, status, data, kind }) => {
  if (!hasTemplate) {
    return TemplateRequestStatus.EMPTY;
  }

  return status === TemplateRequestStatus.LOADED && !!kind && data.kind !== kind
    ? TemplateRequestStatus.OTHER_KIND
    : status;
};

const getDocumentSides = summaryData =>
  _.compact([
    _.isNil(summaryData?.old) ? null : TemplateSide.BEFORE,
    _.isNil(summaryData?.new) ? null : TemplateSide.AFTER,
  ]);

/** The sides of a change's Prism document that exist, from its TemplateJson summary. */
export const getTemplateJsonSides = detail =>
  getDocumentSides(
    _.find(detail?.changes, { field: FormTemplateField.TEMPLATE_JSON })?.summary?.data,
  );

/** The changed parts by group, in display order; empty groups dropped. */
const formatPartTotals = counts =>
  TEMPLATE_PART_GROUPS.filter(({ countKey }) => counts?.[countKey] > 0)
    .map(({ title, countKey }) => `${title}: ${counts[countKey]}`)
    .join(' · ') || NO_PARTS_LABEL;

/** Each side's part totals, "Before: Dynamic fields: 3 · Text: 12", for a re-imported version. */
export const getPartTotalsLines = totals =>
  TEMPLATE_SIDES.map(side => ({
    side,
    text: `${TEMPLATE_SIDE_LABEL[side]}: ${formatPartTotals(totals?.[side])}`,
  }));

/** Why a Prism change lists no parts, or null when it has parts to list. */
export const getPrismChangesMessage = templateChanges => {
  const statusMessage = PRISM_STATUS_MESSAGE[templateChanges.status];

  if (statusMessage) {
    return statusMessage;
  }

  return templateChanges.kind === TemplateChangeKind.PRISM && !_.isEmpty(templateChanges.parts)
    ? null
    : NO_PART_CHANGES_MESSAGE;
};

export const getPartChangeLabelProps = change => ({
  title: PART_CHANGE_LABEL[change] ?? change,
  variant: PART_CHANGE_VARIANT[change] ?? LabelVariant.GRAY,
});

const formatNodeType = nodeType => _.upperFirst(_.lowerCase(nodeType));

const formatTableSize = state =>
  _.isNil(state?.rows) || _.isNil(state?.columns) ? null : `${state.rows} × ${state.columns}`;

const getPartState = part => part.after ?? part.before;

const isFieldGroup = group =>
  group === TemplatePartGroup.DYNAMIC_FIELD || group === TemplatePartGroup.SIGNATURE;

const getFieldName = part => part.label ?? getPartState(part)?.path ?? null;

/** A field by its label, a text block by its opening words, anything else by what it is. */
const PART_NAME = {
  [TemplatePartGroup.DYNAMIC_FIELD]: getFieldName,
  [TemplatePartGroup.SIGNATURE]: getFieldName,
  [TemplatePartGroup.TEXT]: part =>
    _.trim(part.preview) ? `“${part.preview}”` : EMPTY_PARAGRAPH_LABEL,
  [TemplatePartGroup.IMAGE]: () => IMAGE_PART_NAME,
  [TemplatePartGroup.LAYOUT]: part =>
    _.compact([formatNodeType(part.nodeType), formatTableSize(getPartState(part))]).join(' '),
};

const formatPosition = location => `position ${location.index + 1}`;

const getMoveNote = ({ from, to }) => {
  if (from.region !== to.region) {
    return `${REGION_LABEL[from.region]} → ${REGION_LABEL[to.region]}`;
  }

  const note = `${_.upperFirst(formatPosition(from))} → ${formatPosition(to)}`;

  return from.parent === to.parent ? note : `${note} ${ANOTHER_BLOCK_NOTE}`;
};

const getContainerNote = container => {
  if (!container) {
    return null;
  }

  if (container.nodeType === TABLE_NODE_TYPE) {
    return IN_TABLE_NOTE;
  }

  return _.trim(container.preview)
    ? `In “${_.truncate(container.preview, { length: CONTAINER_PREVIEW_LENGTH, omission: '…' })}”`
    : null;
};

const getReboundNote = rebound => {
  const previousLabel = rebound.before?.label;

  return previousLabel && previousLabel !== rebound.label ? `Was ${previousLabel}` : REBOUND_NOTE;
};

const getResizeNote = edited => {
  const previousSize = formatTableSize(edited.before);

  return previousSize ? `Was ${previousSize}` : null;
};

/** What the row's kind word does not say: where it moved, what it read before, what else changed. */
const getChangeNotes = (part, changesByType) => {
  const moved = changesByType[TemplatePartChange.MOVED];
  const rebound = changesByType[TemplatePartChange.REBOUND];
  const edited = changesByType[TemplatePartChange.EDITED];

  return [
    moved?.move ? getMoveNote(moved.move) : null,
    rebound ? getReboundNote(rebound) : null,
    edited && part.group === TemplatePartGroup.LAYOUT ? getResizeNote(edited) : null,
    changesByType[TemplatePartChange.REFORMATTED] ? FORMATTING_CHANGED_NOTE : null,
  ];
};

/** Where a part sits and what else is worth knowing, joined by " · ". */
const getPartWhere = (part, changesByType) => {
  const signer = part.group === TemplatePartGroup.SIGNATURE ? getPartState(part)?.signer : null;

  return _.compact([
    part.region === TemplateRegion.BODY ? null : REGION_LABEL[part.region],
    part.group === TemplatePartGroup.TEXT ? formatNodeType(part.nodeType) : null,
    getContainerNote(part.container),
    signer ? `Signed by ${signer}` : null,
    ...getChangeNotes(part, changesByType),
  ]).join(' · ');
};

/** One row per part: the host lists a part once per way it changed (re-bound and reformatted). */
const toPartRow = changes => {
  const [part] = changes;
  const changesByType = _.keyBy(changes, 'change');
  const kind = PART_ROW_KINDS.find(rowKind =>
    changes.some(change => PART_ROW_KIND_BY_CHANGE[change.change] === rowKind),
  );

  return {
    id: part.id,
    group: part.group,
    kind: kind ?? PartRowKind.CHANGED,
    name: PART_NAME[part.group]?.(part) ?? formatNodeType(part.nodeType),
    where: getPartWhere(part, changesByType),
    // A top-level block has no container: the header, body or footer it sits in is its container.
    containerKey: part.container?.id ?? part.region,
    partCount: 1,
    isFieldName: isFieldGroup(part.group),
    isRemoved: kind === PartRowKind.REMOVED,
  };
};

// The whole "where" is in the key, so a merged row never hides a signer, a move or an old label.
const getRowMergeKey = row => [row.group, row.kind, row.containerKey, row.where].join('|');

/** "Borrower name, Loan amount" / "3 parts · In “Borrower(s): …”". */
const mergeRows = rows =>
  rows.length === 1
    ? rows[0]
    : {
        ...rows[0],
        name: _.uniq(_.map(rows, 'name')).join(', '),
        where: _.compact([`${rows.length} parts`, rows[0].where]).join(' · '),
        partCount: rows.length,
      };

/** Parts of one kind in the same block read as one row. */
const mergeSameBlockRows = rows => {
  const rowsByKey = _.groupBy(rows, getRowMergeKey);

  return _.uniq(rows.map(getRowMergeKey)).map(key => mergeRows(rowsByKey[key]));
};

const countParts = rows => _.sumBy(rows, 'partCount');

const getKindCountsNote = rows =>
  PART_ROW_KINDS.map(kind => ({ kind, count: countParts(rows.filter(row => row.kind === kind)) }))
    .filter(({ count }) => count > 0)
    .map(({ kind, count }) => `${count} ${_.lowerCase(PART_ROW_KIND_LABEL[kind])}`)
    .join(' · ');

/** The changed parts by group in display order, each group with its kind counts. */
export const getTemplatePartGroups = parts => {
  const changesById = _.groupBy(parts ?? [], 'id');
  const rows = mergeSameBlockRows(
    _.uniq(_.map(parts ?? [], 'id')).map(id => toPartRow(changesById[id])),
  );

  return TEMPLATE_PART_GROUPS.map(({ group, title }) => {
    const groupRows = rows.filter(row => row.group === group);

    return { group, title, rows: groupRows, summary: getKindCountsNote(groupRows) };
  }).filter(({ rows: groupRows }) => groupRows.length > 0);
};

/** "7 parts in 4 groups". */
export const getPartsSubtitle = groups => {
  const partCount = _.sumBy(groups, group => countParts(group.rows));

  return `${partCount} ${partCount === 1 ? 'part' : 'parts'} in ${groups.length} ${
    groups.length === 1 ? 'group' : 'groups'
  }`;
};

export const formatChecksum = sha256 => `${sha256.slice(0, SHORT_CHECKSUM_LENGTH)}…`;

/** A file's facts as DataDisplay pairs; PDF pages and a PDF's "Uploaded" label, an HTML's "Saved". */
export const getFileFacts = (facts, isPdf) =>
  _.compact([
    _.isNil(facts.sizeBytes)
      ? null
      : { title: FileFactLabel.SIZE, value: formatFileSize(facts.sizeBytes) },
    isPdf && !_.isNil(facts.pages) ? { title: FileFactLabel.PAGES, value: `${facts.pages}` } : null,
    facts.savedAt
      ? {
          title: isPdf ? FileFactLabel.UPLOADED : FileFactLabel.SAVED,
          value: formatRowDateTime(facts.savedAt),
        }
      : null,
  ]);

export const getFieldMappingTitle = count => `${FIELD_MAPPING_TITLE} (${count})`;

/** "Previous version · Jul 1, 2025 · 9:58 AM": the side, and when its file was saved when known. */
export const getFileModalTitle = (side, facts) => {
  const title = FILE_MODAL_TITLE[side] ?? '';

  return facts?.savedAt ? `${title} · ${formatRowDateTime(facts.savedAt)}` : title;
};

export const isSameFile = files =>
  !!files?.before?.sha256 && files.before.sha256 === files.after?.sha256;

export const getHtmlChange = files => {
  if (!files.before) {
    return HtmlChange.ADDED;
  }

  if (!files.after) {
    return HtmlChange.REMOVED;
  }

  return isSameFile(files) ? HtmlChange.UNCHANGED : HtmlChange.REPLACED;
};

const formatMappingPosition = position =>
  position
    ? `Page ${position.page} (${_.round(position.x, POSITION_DECIMALS)}, ${_.round(
        position.y,
        POSITION_DECIMALS,
      )})`
    : null;

/** The field-mapping rows: a stable `id` per entry and its before/after position lines. */
export const getFieldMappingRows = fieldMapping =>
  (fieldMapping ?? []).map((entry, index) => ({
    ...entry,
    id: `${entry.key}:${index}`,
    positionLines: [
      {
        key: 'position',
        before: formatMappingPosition(entry.before),
        after: formatMappingPosition(entry.after),
      },
    ],
  }));

const getSignatureFieldLabel = field => {
  const signer = field.getAttribute(HtmlTemplateFieldAttribute.SIGNER);

  return signer ? `${SIGNATURE_FIELD_LABEL} (${signer})` : SIGNATURE_FIELD_LABEL;
};

const getTemplateFieldLabel = (field, dynamicFieldsNamesById) =>
  field.getAttribute(HtmlTemplateFieldAttribute.TYPE) === HtmlTemplateFieldType.SIGNATURE
    ? getSignatureFieldLabel(field)
    : (getDynamicFieldCatalogName({
        dynamicFieldsNamesById,
        value: field.getAttribute(HtmlTemplateFieldAttribute.DYNAMIC_FIELD_ID),
      }) ?? UNKNOWN_DYNAMIC_FIELD_LABEL);

/**
 * A stored HTML template with each dynamic field's and signature's empty slot filled with its name,
 * and `markCss` added to highlight them; the markup as it was when it has no such field.
 * DOMParser builds an inert document: nothing in the template runs while it is read.
 */
export const markHtmlTemplateFields = ({ html, dynamicFieldsNamesById, markCss }) => {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const fields = document.querySelectorAll(HTML_TEMPLATE_FIELD_SELECTOR);

  if (fields.length === 0) {
    return html;
  }

  fields.forEach(field => {
    const slot = field.querySelector(HTML_TEMPLATE_FIELD_SLOT_SELECTOR) ?? field;

    slot.textContent = getTemplateFieldLabel(field, dynamicFieldsNamesById);
  });

  const style = document.createElement('style');

  style.textContent = markCss;
  document.head.append(style);

  return document.documentElement.outerHTML;
};

/** The side of a change that holds the version it lists: what it saved, or what it deleted. */
const getVersionSide = operation =>
  toOperation(operation) === AuditOperation.DELETED ? TemplateSide.BEFORE : TemplateSide.AFTER;

/** Which of a template's two sides hold one: a Prism summary's old / new document. */
export const getPrismSidePresence = summaryData => {
  const documentSides = getDocumentSides(summaryData);

  return TEMPLATE_SIDES.map(side => documentSides.includes(side));
};

/** Which of a PDF or HTML template's two sides hold a file. */
const getFileSidePresence = files => [!!files?.before, !!files?.after];

/**
 * A change's preview: before and after, a side without a template (a Create's before) saying so
 * unread. `presence` is whether each side holds one, older first.
 */
export const getChangePreviewPanes = presence =>
  TEMPLATE_SIDES.map((side, index) => ({
    side,
    title: PREVIEW_PANE_TITLE[side],
    hasDocument: presence[index],
  }));

/** A compare's preview: the listed version against the form as it is now. */
export const getVersionPreviewPanes = (operation, presence = [true, true]) => [
  { side: getVersionSide(operation), title: VERSION_PANE_TITLE, hasDocument: presence[0] },
  {
    side: TemplateSide.CURRENT,
    title: PREVIEW_PANE_TITLE[TemplateSide.CURRENT],
    hasDocument: presence[1],
  },
];

const TEMPLATE_FILES = {
  [TemplateChangeKind.PDF]: template => template.pdf,
  [TemplateChangeKind.HTML]: template => template.html,
};

/** Which sides of a template changes answer hold a template to preview; a Prism answer, both. */
export const getTemplatePreviewPresence = templateChanges => {
  const getFiles = TEMPLATE_FILES[templateChanges.kind];

  return getFiles ? getFileSidePresence(getFiles(templateChanges)) : [true, true];
};

// A PDF's sample render places values by its field mapping, so a mapping change shows on the page.
const HAS_TEMPLATE_PREVIEW = {
  [TemplateChangeKind.PRISM]: () => true,
  [TemplateChangeKind.PDF]: ({ pdf }) =>
    !isSameFile(pdf) || !_.isEmpty(pdf.fieldMapping) || !!pdf.fieldMappingUnreadable,
  [TemplateChangeKind.HTML]: ({ html }) => getHtmlChange(html) !== HtmlChange.UNCHANGED,
};

/** Whether the two sides render differently: a re-saved file, mapping untouched, does not. */
export const hasTemplatePreview = templateChanges =>
  !!HAS_TEMPLATE_PREVIEW[templateChanges.kind]?.(templateChanges);

const PART_GROUP_ORDER = _.fromPairs(
  TEMPLATE_PART_GROUPS.map(({ group }, index) => [group, index]),
);

/**
 * The changed parts numbered for a Prism preview, in the part list's order: one mark per part, in
 * the panes it exists in ("Dynamic field added", only in the newer pane).
 */
export const getPreviewMarks = parts => {
  const changesById = _.groupBy(parts ?? [], 'id');
  const rows = _.sortBy(
    _.uniq(_.map(parts ?? [], 'id')).map(id => toPartRow(changesById[id])),
    row => PART_GROUP_ORDER[row.group],
  );

  return rows.map((row, index) => ({
    id: row.id,
    number: index + 1,
    title: `${PART_GROUP_SINGULAR[row.group]} ${_.lowerCase(PART_ROW_KIND_LABEL[row.kind])}`,
    name: row.name,
    isFieldName: row.isFieldName,
    panes: MARK_PANES_BY_ROW_KIND[row.kind] ?? PREVIEW_PANES,
  }));
};

export const getChangesOnPageTitle = pageNumber => `Changes on page ${pageNumber}`;

export const getMissingPageMessage = pageNumber => `No page ${pageNumber} on this side.`;

/** "1 change on page 2". */
export const getPageChangesLabel = (pageNumber, count) =>
  `${count} ${count === 1 ? 'change' : 'changes'} on page ${pageNumber}`;

/** "Preview · before → after · Oct 28, 2026 · 11:00 AM": what is compared, and the change's time. */
export const getPreviewModalTitle = (kind, occurredAtUtc) =>
  _.compact([PREVIEW_MODAL_TITLE[kind], formatRowDateTime(occurredAtUtc)]).join(' · ');

export const hasTemplateDifferences = template =>
  !!template && template.kind !== TemplateChangeKind.NONE;

export const hasCompareDifferences = compare =>
  hasTemplateDifferences(compare.template) || !_.isEmpty(compare.fields);

/** The compare's fields as the shared diff reads them: this version was, the current form became. */
export const getCompareFieldChanges = fields =>
  (fields ?? []).map(({ field, version, current }) => ({ field, old: version, new: current }));

/** "3 other fields differ" under the template's differences, "1 field differs" without them. */
export const getFieldDifferencesTitle = (count, hasTemplate) =>
  `${count} ${hasTemplate ? 'other ' : ''}${count === 1 ? 'field differs' : 'fields differ'}`;

/** The template's differences count, "3 parts in 2 groups", for a Prism template. */
export const getTemplateDifferencesSubtitle = template =>
  template.kind === TemplateChangeKind.PRISM && !_.isEmpty(template.parts)
    ? getPartsSubtitle(getTemplatePartGroups(template.parts))
    : null;

/** The version picker's options, newest first, each with its row's scope chips. */
export const getVersionOptions = (rows, getRowDecoration) =>
  sortEntriesByTime(rows, SortOrder.DESC).map(row => ({
    ...row,
    chips: getRowDecoration?.(row)?.chips ?? null,
  }));

/** A row's actions: compare its version with the current form (`onCompare(row, context)`). */
export const getFormRowActions = (row, context, onCompare) => [
  {
    text: COMPARE_WITH_CURRENT_LABEL,
    'data-testid': FormChangeHistoryTestIds.COMPARE_ACTION,
    onClick: () => onCompare(row, context),
  },
];

export const getVersionOptionLabel = option =>
  `${formatRowDateTime(option.occurredAtUtc)} · ${getOperationLabel(toOperation(option.operation))}`;
